import React, { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { WEB_RECOVERY_PATH } from '../../lib/nativeRecoveryContinuation';

interface RequestResetLinkProps {
  client: SupabaseClient;
}

/**
 * Request-a-new-reset-link form for the recovery route. Sends through the
 * route's ephemeral client with the same redirect contract the app and the
 * Login forgot-password flow use. The confirmation is deliberately neutral —
 * it must not reveal whether the address has an account.
 */
const RequestResetLink: React.FC<RequestResetLinkProps> = ({ client }) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending || sent) return;
    setSending(true);
    setError(null);
    try {
      const redirectTo = `${window.location.origin}${WEB_RECOVERY_PATH}`;
      const { error: sendError } = await client.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (sendError) throw sendError;
      setSent(true);
    } catch {
      setError('Could not send a reset email right now. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="utility-message utility-message--success" role="status">
        If an account exists for that email, we've sent a new reset link. Check your inbox
        (and spam folder) — the link works in any browser.
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <div aria-live="polite">
        {error && (
          <div className="utility-message utility-message--error" role="alert">
            {error}
          </div>
        )}
      </div>
      <label className="utility-field">
        <span className="utility-label">Email</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(null); }}
          className="utility-input"
          disabled={sending}
          required
        />
      </label>
      <button type="submit" className="utility-button" disabled={sending || !email.trim()}>
        {sending ? 'Sending...' : 'Send a new reset link'}
      </button>
    </form>
  );
};

export default RequestResetLink;
